import { Shield, Cloud, Bot, Lock, ArrowUpRight, Network } from "lucide-react";

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-[#0B0E14]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-16">
          <span className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#00FFA3]">
            // Capabilities
          </span>
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mt-4 leading-tight">
            One mission. Three battle-tested disciplines.
          </h2>
          <p className="text-slate-400 mt-5 text-lg">
            From hardening your perimeter to deploying autonomous AI agents, we
            bring operational discipline to every layer of your stack.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {/* Cybersecurity - large */}
          <div
            data-testid="service-cybersecurity"
            className="md:col-span-2 md:row-span-2 group relative bg-[#151921] border border-white/10 hover:border-[#00FFA3]/40 transition-colors p-8 md:p-10 flex flex-col justify-between min-h-[340px] overflow-hidden"
          >
            <div className="absolute inset-0 grid-bg opacity-20 group-hover:opacity-40 transition-opacity" />
            <div className="relative z-10">
              <div className="w-14 h-14 flex items-center justify-center border border-[#00FFA3]/40 bg-[#00FFA3]/5 mb-6">
                <Shield size={26} strokeWidth={1.5} className="text-[#00FFA3]" />
              </div>
              <h3 className="font-heading font-bold text-2xl md:text-3xl text-white">
                Cybersecurity
              </h3>
              <p className="text-slate-400 mt-4 max-w-md leading-relaxed">
                Threat detection, penetration testing, incident response, and
                Zero Trust architecture. We secure your perimeter the way we
                secured the front line — relentlessly.
              </p>
            </div>
            <ul className="relative z-10 mt-8 grid grid-cols-2 gap-3">
              {[
                "Penetration Testing",
                "SOC Monitoring",
                "Compliance & Audit",
                "Incident Response",
              ].map((i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 text-sm text-slate-300"
                >
                  <Lock size={13} className="text-[#00FFA3]" />
                  {i}
                </li>
              ))}
            </ul>
          </div>

          {/* Cloud */}
          <div
            data-testid="service-cloud"
            className="md:col-span-2 group bg-[#151921] border border-white/10 hover:border-[#00E5FF]/40 transition-colors p-8 flex flex-col justify-between min-h-[160px]"
          >
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 flex items-center justify-center border border-[#00E5FF]/40 bg-[#00E5FF]/5">
                <Cloud size={22} strokeWidth={1.5} className="text-[#00E5FF]" />
              </div>
              <ArrowUpRight
                size={20}
                className="text-slate-600 group-hover:text-[#00E5FF] transition-colors"
              />
            </div>
            <div className="mt-6">
              <h3 className="font-heading font-bold text-xl md:text-2xl text-white">
                Cloud Infrastructure
              </h3>
              <p className="text-slate-400 mt-2 text-sm leading-relaxed">
                Secure cloud migration, architecture, and DevSecOps across AWS,
                Azure, and GCP — built for scale and resilience.
              </p>
            </div>
          </div>

          {/* AI Agents */}
          <div
            data-testid="service-ai"
            className="md:col-span-2 group bg-[#151921] border border-white/10 hover:border-[#00E5FF]/40 transition-colors p-8 flex flex-col justify-between min-h-[160px]"
          >
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 flex items-center justify-center border border-[#00E5FF]/40 bg-[#00E5FF]/5">
                <Bot size={22} strokeWidth={1.5} className="text-[#00E5FF]" />
              </div>
              <ArrowUpRight
                size={20}
                className="text-slate-600 group-hover:text-[#00E5FF] transition-colors"
              />
            </div>
            <div className="mt-6">
              <h3 className="font-heading font-bold text-xl md:text-2xl text-white">
                AI & Autonomous Agents
              </h3>
              <p className="text-slate-400 mt-2 text-sm leading-relaxed">
                We design, build, and deploy custom AI agents that automate
                workflows, analyze threats, and scale your operations.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-10 flex items-center gap-3 text-slate-400">
          <Network size={16} className="text-[#00FFA3]" />
          <span className="text-sm">
            Need a tailored engagement?{" "}
            <button
              data-testid="services-contact-link"
              onClick={() => scrollTo("contact")}
              className="text-[#00FFA3] hover:text-[#00E5FF] transition-colors font-medium underline underline-offset-4"
            >
              Talk to our team
            </button>
          </span>
        </div>
      </div>
    </section>
  );
};
